// useEmployeeCardLogic.ts
import { useState, useCallback } from "react";

import { Employee } from "0.Mine/3.Interfaces/Employee";

export interface EmployeeCardLogicProps {
  employee: Employee;
  index: number;
  onUpdate: (employee: Employee) => void;
  onDelete: (employeeId: number) => void;
}

export interface EmployeeCardLogicReturn {
  editing: boolean;
  editingData: Employee | null;
  expanded: boolean;
  startEditing: () => void;
  cancelEditing: () => void;
  saveEditing: () => void;
  toggleExpanded: () => void;
  handleDelete: () => void;
  handleEditingDataChange: (field: keyof Employee, value: string) => void;
  handleScheduleChange: (newSchedule: any) => void;
}

export const useEmployeeCardLogic = ({
  employee,
  index,
  onUpdate,
  onDelete,
}: EmployeeCardLogicProps): EmployeeCardLogicReturn => {
  const [expanded, setExpanded] = useState<boolean>(false);
  const [editing, setEditing] = useState<boolean>(false);
  const [editingData, setEditingData] = useState<Employee | null>(null);

  const startEditing = useCallback(() => {
    setEditing(true);
    setEditingData({ ...employee, serviceNames: [...employee.serviceNames] });
  }, []);

  const cancelEditing = useCallback(() => {
    setEditing(false);
    setEditingData(null);
  }, []);

  const saveEditing = useCallback(() => {
    if (editingData) {
      // Filter out empty services
      const filteredServices = editingData.serviceNames.filter((s) => s.trim() !== "");
      const updatedEmployee: Employee = { ...editingData, serviceNames: filteredServices };
      onUpdate(updatedEmployee);
      setEditing(false);
      setEditingData(null);
    }
  }, [editingData, onUpdate]);

  const handleScheduleChange = useCallback(
    (newSchedule: any) => {
      const updatedEmployee = { ...employee, workingHours: newSchedule };
      onUpdate(updatedEmployee);
    },
    [employee, onUpdate]
  );

  const toggleExpanded = useCallback(() => {
    setExpanded((prev) => !prev);
  }, []);

  const handleDelete = useCallback(() => {
    onDelete(employee.id);
  }, [employee.id]);

  const handleEditingDataChange = useCallback((field: keyof Employee, value: string) => {
    if (editingData) {
      setEditingData((prev) => ({
        ...prev!,
        [field]: value,
      }));
    }
  }, []);

  return {
    editing,
    editingData,
    expanded,
    startEditing,
    cancelEditing,
    saveEditing,
    toggleExpanded,
    handleDelete,
    handleEditingDataChange,
    handleScheduleChange,
  };
};
